import { featurebaseFeatureBoardUrl } from '@griply/utils';
import { CircularProgress } from '@mui/material';
import { differenceInDays, isBefore } from 'date-fns';
import { useLogout, useUserHadTrial } from 'features/user';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  Bug16,
  Bulb16,
  ChatFeedback16,
  ChevronDown,
  CommandButton16,
  Discord16,
  Dot,
  ExitLogOutArrow16,
  GiftBox16,
  LifeAreaRocket,
  LifeRingHelpSupport16,
  Mail16,
  Reddit16,
  SettingsRound16,
} from 'shared/assets/icons';
import { Collapse } from 'shared/components/ui/collapse';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import {
  discordLink,
  helpCenterLink,
  redditLink,
  shortcutsLink,
} from 'shared/constants';
import { useOpenFeedbackDialog } from 'shared/contexts/feedback-dialog';
import { useOpenPremiumDialogWithPriceSelection } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useOpenSettings } from 'shared/contexts/user-settings';
import { useNewVersionAvailable } from 'shared/contexts/version';
import { useWhatsNew } from 'shared/contexts/whats-new';
import { useFeaturebaseTokenQuery } from 'shared/hooks/queries/use-featurebase-token-query';
import { useFeaturebaseWidget } from 'shared/hooks/use-featurebase-widget';
import { useUser } from 'shared/hooks/use-user';
import { Entitlements } from 'shared/types/entitlements';
import { PriceInterval } from 'shared/types/price';
import { Environments, isEnvironment } from 'shared/utils/is-environment';

import { OutlineOption } from './outline-option';
import * as Styled from './user-context-menu.style';

export type UserContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'location' | 'onOutside'
> & {
  onClose: () => void;
};

export const UserContextMenu: React.FC<UserContextMenuProps> = ({
  onClose,
  referenceElement,
  position = 'bottom-start',
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const user = useUser();
  const userHadTrial = useUserHadTrial();
  const newVersionAvailable = useNewVersionAvailable();

  const openPremiumDialog = useOpenPremiumDialogWithPriceSelection();
  const logout = useLogout();
  const openSettings = useOpenSettings();
  const openFeedbackDialog = useOpenFeedbackDialog();
  const { openWhatsNew } = useWhatsNew();

  const {
    data: tokenData,
    refetch: fetchToken,
    isFetching,
  } = useFeaturebaseTokenQuery();
  const { openWidget, isReady: isWidgetReady } = useFeaturebaseWidget(
    tokenData?.token,
  );
  const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(false);

  const userIsPremium = useMemo(
    () => user?.entitlements?.includes(Entitlements.Premium),
    [user?.entitlements],
  );

  const activeLifetimeTrial = useMemo(() => {
    const activeSubscription = user?.subscriptions
      // filter out the expired subscriptions
      ?.filter(({ expiresAt }) => !expiresAt || isBefore(today, expiresAt))
      // sort them from to have the latest purchase first
      ?.sort(
        ({ purchasedAt: purchasedAtA }, { purchasedAt: purchasedAtB }) =>
          new Date(purchasedAtB).getTime() - new Date(purchasedAtA).getTime(),
      )[0];
    return activeSubscription?.productId?.includes('custom')
      ? activeSubscription
      : undefined;
  }, [today, user?.subscriptions]);

  const onSettings = () => {
    openSettings();
    onClose();
  };

  const onFeaturebaseClick = async () => {
    if (tokenData?.token) {
      setIsFeedbackExpanded((prev) => !prev);
      return;
    }

    const { data } = await fetchToken();
    if (data?.token) {
      setIsFeedbackExpanded(true);
    }
  };

  const onFeedback = () => {
    if (!tokenData?.token) {
      return;
    }
    const returnTo = encodeURIComponent(featurebaseFeatureBoardUrl);
    const ssoUrl = `https://griply.featurebase.app/api/v1/auth/access/jwt?jwt=${tokenData.token}&return_to=${returnTo}`;
    window.open(ssoUrl, '_blank');
    onClose();
  };

  const onReportBug = () => {
    onClose();
    openWidget();
  };

  // @ts-ignore -- reload(true) should forceGet: https://developer.mozilla.org/en-US/docs/Web/API/Location/reload#forceget
  const onReload = () => window.location.reload(true);

  const onLogout = () => {
    logout();
    onClose();
  };

  const onShortcutsLink = () => {
    window.open(shortcutsLink, '_blank');
    onClose();
  };

  const onRedditLink = () => {
    window.open(redditLink, '_blank');
    onClose();
  };

  const onDiscordLink = () => {
    window.open(discordLink, '_blank');
    onClose();
  };

  const onHelpCenterLink = () => {
    window.open(helpCenterLink, '_blank');
    onClose();
  };

  const onIAP = () => {
    openPremiumDialog(activeLifetimeTrial ? PriceInterval.Lifetime : undefined);
    onClose();
  };

  const onContactSupport = () => {
    openFeedbackDialog();
    onClose();
  };

  const onShowWhatsNew = () => {
    openWhatsNew();
    onClose();
  };

  return (
    <PopupMenu
      referenceElement={referenceElement}
      position={position}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onSettings}
            start={<Icon icon={SettingsRound16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.settings')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onShortcutsLink}
            start={<Icon icon={CommandButton16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.keyboard-shortcuts')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      {newVersionAvailable && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onReload}
              start={<Icon icon={ArrowDown} />}
              end={<Icon icon={Dot} />}
              endColor={Colors.Error}
              tabIndex={0}
            >
              {t('context-menus.user.labels.reload')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      {(!userIsPremium || !!activeLifetimeTrial) && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onIAP}
              start={<Icon icon={LifeAreaRocket} />}
              startColor={Colors.Warning}
              tabIndex={0}
            >
              <Styled.PremiumLabelContainer>
                {t(
                  activeLifetimeTrial
                    ? 'context-menus.user.labels.purchase-premium'
                    : userHadTrial
                      ? 'context-menus.user.labels.upgrade-premium'
                      : 'context-menus.user.labels.try-premium-free',
                )}
                {!!activeLifetimeTrial && !!activeLifetimeTrial.expiresAt && (
                  <Styled.LifetimeTrialLabel>
                    {t('context-menus.user.labels.trial-time-left', {
                      count: differenceInDays(
                        activeLifetimeTrial.expiresAt,
                        today,
                      ),
                    })}
                  </Styled.LifetimeTrialLabel>
                )}
              </Styled.PremiumLabelContainer>
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDiscordLink}
            start={<Icon icon={Discord16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.discord-community')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onRedditLink}
            start={<Icon icon={Reddit16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.reddit-community')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onHelpCenterLink}
            start={<Icon icon={LifeRingHelpSupport16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.help-center')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onFeaturebaseClick}
            start={<Icon icon={ChatFeedback16} />}
            end={
              isFetching ? (
                <CircularProgress color="primary" size={16} />
              ) : (
                <Styled.ChevronIcon $isOpen={isFeedbackExpanded}>
                  <Icon icon={ChevronDown} color="inherit" />
                </Styled.ChevronIcon>
              )
            }
            disabled={isFetching}
            tabIndex={0}
          >
            {t('context-menus.user.labels.share-feedback')}
          </PopupMenuButton>
        </PopupMenuListItem>

        <Collapse isOpen={isFeedbackExpanded}>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onFeedback}
              start={<Icon icon={Bulb16} />}
              indentationLevel={1}
              tabIndex={0}
            >
              {t('context-menus.user.labels.request-feature')}
            </PopupMenuButton>
          </PopupMenuListItem>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onReportBug}
              start={<Icon icon={Bug16} />}
              disabled={!isWidgetReady}
              indentationLevel={1}
              tabIndex={0}
            >
              {t('context-menus.user.labels.report-bug')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </Collapse>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onContactSupport}
            start={<Icon icon={Mail16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.contact-support')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onShowWhatsNew}
            start={<Icon icon={GiftBox16} />}
            tabIndex={0}
          >
            {t('whats-new.title')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      {(isEnvironment(Environments.Development) ||
        isEnvironment(Environments.Local)) && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuTitle title="Debug options" isSubTitle />
          <OutlineOption />
        </PopupMenuList>
      )}

      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onLogout}
            start={<Icon icon={ExitLogOutArrow16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.logout')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
